import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';

// City Council Chambers — the mayor's office and the town's meeting hall.
// Mostly flavor for now; exit returns to the town square.

export function createCouncilScene(){
  return () => new CouncilScene();
}

class CouncilScene {
  constructor(){
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE8DDD0);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 12;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.45;

    this._buildRoom();
    this._buildFurniture();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3.2);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.clerk = this._makeClerk();
    this.scene.add(this.clerk.root);

    this.hotspots = [
      { pos: this.clerk.root.position.clone(), r: 2.3, label: '🗣 talk to Clerk Crumb', action: () => this._talkToClerk() },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to town', action: () => this.manager.goTo('town', { from: 'council' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.8));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.5);
    key.position.set(0, 10, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), new THREE.MeshLambertMaterial({ color: 0x8A7A6A }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    const wallMat = new THREE.MeshLambertMaterial({ color: 0xD8C8B8 });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(16, 8), wallMat);
    back.position.set(0, 4, -6); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-8, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(8, 4, 0); this.scene.add(sideR);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to Town', 26, '#6B4A2F', 2.6, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildFurniture(){
    // big council table
    const table = new THREE.Group(); table.position.set(0, 0, -1.5); this.scene.add(table);
    box(5.5, .15, 2.2, 0x6E4A35, 0, .8, 0, table);
    cyl(.15, .12, .8, 0x5A4A2A, -2.2, .4, 0, table, 8);
    cyl(.15, .12, .8, 0x5A4A2A,  2.2, .4, 0, table, 8);
    cyl(.15, .12, .8, 0x5A4A2A, -2.2, .4, 1.6, table, 8);
    cyl(.15, .12, .8, 0x5A4A2A,  2.2, .4, 1.6, table, 8);
    // chairs around table
    for (let x = -2; x <= 2; x += 1.33){
      this._chair(new THREE.Vector3(x, 0, -.3), 0);
      this._chair(new THREE.Vector3(x, 0, -2.7), Math.PI);
    }
    // mayor's podium
    const podium = new THREE.Group(); podium.position.set(0, 0, -4.2); this.scene.add(podium);
    box(1.4, 1.0, .8, 0x6E4A35, 0, .5, 0, podium);
    box(1.2, .08, .1, 0x4A3A2A, 0, .95, .4, podium);
    // town seal on back wall (facing into the room)
    const seal = new THREE.Mesh(new THREE.CircleGeometry(.9, 32), new THREE.MeshLambertMaterial({ color: 0xFFD700, side: THREE.DoubleSide }));
    seal.position.set(0, 3.2, -5.9); seal.rotation.y = Math.PI; this.scene.add(seal);
    const inner = new THREE.Mesh(new THREE.CircleGeometry(.6, 32), new THREE.MeshLambertMaterial({ color: 0xB5824E, side: THREE.DoubleSide }));
    inner.position.set(0, 3.2, -5.85); inner.rotation.y = Math.PI; this.scene.add(inner);
  }

  _chair(pos, rot){
    const g = new THREE.Group(); g.position.copy(pos); g.rotation.y = rot; this.scene.add(g);
    box(.5, .1, .5, 0x5A4A2A, 0, .45, 0, g);
    box(.5, .55, .08, 0x5A4A2A, 0, .75, -.24, g);
    cyl(.04, .04, .45, 0x4A3A2A, -.2, .22, -.2, g, 5);
    cyl(.04, .04, .45, 0x4A3A2A,  .2, .22, -.2, g, 5);
    cyl(.04, .04, .45, 0x4A3A2A, -.2, .22,  .2, g, 5);
    cyl(.04, .04, .45, 0x4A3A2A,  .2, .22,  .2, g, 5);
  }

  _makeClerk(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.6, 1.1);
      sphere(.07, 0x2E2018, -.16, 1.18, .3, g, 8);
      sphere(.07, 0x2E2018,  .16, 1.18, .3, g, 8);
      // glasses
      cyl(.06, .06, .04, 0x333333, -.16, 1.18, .3, g, 8);
      cyl(.06, .06, .04, 0x333333,  .16, 1.18, .3, g, 8);
      box(.12, .02, .02, 0x333333, 0, 1.18, .3, g);
      // vest
      box(.62, .7, .38, 0x3D5FC0, 0, .45, 0, g);
      box(.16, .7, .4, 0x3D5FC0, -.24, .45, 0, g);
      box(.16, .7, .4, 0x3D5FC0,  .24, .45, 0, g);
      stickLegs(g, .5);
    };
    return new Townsperson({
      name: 'Clerk Crumb', line: 'the mayor is in a meeting. probably.',
      pos: new THREE.Vector3(3.2, 0, 0), facing: Math.PI * .7,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _talkToClerk(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.clerk.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Clerk Crumb</h2>
        <div class="cafe-sub">city council</div>
        <p class="q-dnone">"Mayor Muffin keeps the town records here. Stop by anytime to admire the seal."</p>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _panel(html, mode = 'panel'){
    const el = document.createElement('div');
    el.className = mode === 'bare' ? 'cafe-panel bare' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('city council chambers');
    HUD.say('City Council Chambers. Clerk Crumb minds the desk while the mayor is out.');
    HUD.setHint('click to walk · talk to Clerk Crumb or head back to town');
    HUD.hideHintSoon(7000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, this.clerk, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-7, Math.min(7, gp.x));
    gp.z = Math.max(-5, Math.min(5.5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.clerk.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
